import { useEffect, useRef } from "react";
import { themeVar as colors } from "../design-tokens";

/**
 * GATE-004 — the typed-vs-file address mismatch prompt.
 *
 * The case that produced this item: an agent typed `4112 Hideaway Ln`, uploaded a file that was
 * actually for `4117 Desert Garden Drive`, and the app resolved the disagreement silently. The
 * server already detects it — `server/amb_extract.py::_subject_in_source` returns False and the
 * extract result carries `subject_sourced: false` (threaded into the job's grid payload by
 * `server/amb_report.py`). Nothing asked the human which one was the subject.
 *
 * The rule this enforces is the register's, not this component's invention: **never resolve a
 * source conflict silently**. Three explicit exits, no default, no pre-selection.
 */

export interface AddressMismatch {
  /** What the human typed on the intake screen. */
  typed: string;
  /** The subject address the extract actually found in the file — null when the file named no
   * subject at all (the "borrowed a comp" case), which is a mismatch of a different shape. */
  fromFile: string | null;
}

/** Street-suffix and directional variants that mean the same thing. An MLS export spelling out
 * "Lane" while the agent typed "Ln" is not a conflict, and treating it as one would fire this gate
 * on ordinary jobs until people learned to click through it — which is how a safety prompt dies. */
const ADDRESS_SYNONYMS: Record<string, string> = {
  street: "st", avenue: "ave", av: "ave", road: "rd", drive: "dr", lane: "ln", court: "ct",
  circle: "cir", boulevard: "blvd", place: "pl", trail: "trl", parkway: "pkwy", highway: "hwy",
  terrace: "ter", cove: "cv", ridge: "rdg", crossing: "xing", square: "sq", loop: "loop", run: "run",
  north: "n", south: "s", east: "e", west: "w",
  northeast: "ne", northwest: "nw", southeast: "se", southwest: "sw",
  apartment: "apt", suite: "ste", building: "bldg",
};

/** Loose equality for street addresses, compared on WHOLE TOKENS.
 *
 * Case, punctuation, whitespace and the synonyms above never make two addresses different, and a
 * typed address is usually the short form of what the file spells out — so "4112 Hideaway Ln" and
 * "4112 Hideaway Ln, Plano TX 75024" agree.
 *
 * The comparison is token-aligned, not a raw string prefix: "12 Oak" and "12 Oakwood Trail" are two
 * different properties, and a `startsWith` on characters would silently call them one. Anything
 * looser than this resolves real conflicts silently, which is the exact behaviour GATE-004 exists
 * to stop. */
export function addressesAgree(a: string | null | undefined, b: string | null | undefined): boolean {
  // ONE PLACE, TWO CITY NAMES (2026-09-25, owner: "the CAD data is showing Dallas as the address and
  // the MLS data is showing University Park, it's the same area"). University Park is its own
  // municipality inside Dallas County and shares ZIPs with Dallas; the postal city and the taxing
  // city are both correct and routinely differ between sources. When BOTH addresses carry a street
  // part and a ZIP, the same house number and street inside the same ZIP is the same property,
  // whatever city name each source prints -- so the city is left out of the comparison.
  const pa = streetAndZip(a);
  const pb = streetAndZip(b);
  if (pa && pb && pa.zip === pb.zip) return tokensAgree(pa.street, pb.street);
  return tokensAgree(addressTokens(a), addressTokens(b));
}

function tokensAgree(ta: string[], tb: string[]): boolean {
  if (!ta.length || !tb.length) return true; // nothing to disagree about
  const [short, long] = ta.length <= tb.length ? [ta, tb] : [tb, ta];
  return short.every((tok, i) => tok === long[i]);
}

/** "2924 Rosedale Ave, University Park, TX 75205" -> the street's tokens and "75205"; null unless
 * there is a comma-separated street part AND a ZIP at the end. Deliberately strict: an address this
 * cannot split cleanly is compared the old way, whole. */
function streetAndZip(v: string | null | undefined): { street: string[]; zip: string } | null {
  const s = String(v ?? "");
  const comma = s.indexOf(",");
  if (comma < 1) return null;
  const zip = /(\d{5})(?:-\d{4})?\s*$/.exec(s.slice(comma + 1));
  if (!zip) return null;
  const street = addressTokens(s.slice(0, comma));
  return street.length ? { street, zip: zip[1] } : null;
}

/** ZIP+4. A five-digit ZIP and the same ZIP with its four-digit delivery-route suffix are the same
 * postal code, and one source carrying `75254-7943` while the other carries `75254` is not two
 * properties. Left unhandled, this fired the gate on an otherwise perfectly matching address — the
 * ordinary-job false-positive the comment above warns is how a safety prompt dies.
 *
 * Deliberately narrow: exactly 5 digits, a hyphen, exactly 4 digits. A hyphenated unit or street
 * token (`12-B`, `Farm-to-Market`) does not match and is compared unchanged. */
const ZIP_PLUS_FOUR = /^(\d{5})-\d{4}$/;

export function addressTokens(v: string | null | undefined): string[] {
  return normalizeAddress(v)
    .split(" ")
    .filter(Boolean)
    .map((tok) => {
      const zip = ZIP_PLUS_FOUR.exec(tok);
      return zip ? zip[1] : (ADDRESS_SYNONYMS[tok] ?? tok);
    });
}

export function normalizeAddress(v: string | null | undefined): string {
  return String(v ?? "")
    .toLowerCase()
    .replace(/[.,#]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function AddressMismatchPrompt({
  mismatch,
  onUseTyped,
  onUseFile,
  onCancel,
}: {
  mismatch: AddressMismatch;
  onUseTyped: () => void;
  onUseFile: () => void;
  onCancel: () => void;
}) {
  const noSubjectInFile = !mismatch.fromFile;
  const headingRef = useRef<HTMLHeadingElement>(null);

  // The button that raised this gate ("Review what we found") has just unmounted, so focus would
  // otherwise fall to <body> and a keyboard or screen-reader user would hear nothing at all — on
  // the one prompt in the flow that must never be missed.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <section
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="vb-mismatch-heading"
      style={{ marginTop: 20, background: colors.cautionBg, border: `1px solid ${colors.cautionLine}`, borderRadius: 10, padding: 20 }}
    >
      <h2 id="vb-mismatch-heading" ref={headingRef} tabIndex={-1} style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.caution, outline: "none" }}>
        Which one is the subject?
      </h2>

      {noSubjectInFile ? (
        <p style={{ marginTop: 8, fontSize: 14, lineHeight: 1.5 }}>
          You entered <strong>{mismatch.typed}</strong>, but we couldn&apos;t find a record for it in the file you
          uploaded — the file looks like comparables only. We won&apos;t borrow a comparable&apos;s size, year, or
          photo for your subject.
        </p>
      ) : (
        <p style={{ marginTop: 8, fontSize: 14, lineHeight: 1.5 }}>
          You entered <strong>{mismatch.typed}</strong>, but the file you uploaded is for{" "}
          <strong>{mismatch.fromFile}</strong>.
        </p>
      )}

      <p style={{ marginTop: 8, fontSize: 13, color: colors.textSecondary, lineHeight: 1.5 }}>
        We won&apos;t pick for you. Whichever you choose is the address the report is written about, and the other one
        is treated as a comparable or ignored.
      </p>

      <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button className="vb-btn"
          type="button"
          onClick={onUseTyped}
          style={{ padding: "9px 16px", borderRadius: 8, border: `1px solid ${colors.accent}`, background: colors.accent, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
        >
          Use what I typed{noSubjectInFile ? "" : ` — ${mismatch.typed}`}
        </button>
        {!noSubjectInFile && (
          <button className="vb-btn"
            type="button"
            onClick={onUseFile}
            style={{ padding: "9px 16px", borderRadius: 8, border: `1px solid ${colors.accent}`, background: colors.accentSoft, color: colors.accent, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
          >
            Use the file&apos;s — {mismatch.fromFile}
          </button>
        )}
        <button className="vb-btn"
          type="button"
          onClick={onCancel}
          style={{ padding: "9px 16px", borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bgSurface, color: colors.textPrimary, fontSize: 13, cursor: "pointer" }}
        >
          Cancel — choose a different file
        </button>
      </div>

      {noSubjectInFile && (
        <p style={{ marginTop: 10, fontSize: 12, color: colors.textSecondary }}>
          Choosing your typed address opens the review with the subject rows blank and source-tagged &ldquo;You
          entered&rdquo; — fill them in, or add the county/CAD sheet for that address.
        </p>
      )}
    </section>
  );
}
